import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Handshake, Loader2, Mail, Phone, Users, MessageSquare,
  Calendar, ExternalLink, ChevronDown, CheckCircle2, Clock, XCircle, Trophy,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "Pendiente",
  contacted: "Contactado",
  won: "Ganado",
  lost: "Perdido",
};
const STATUS_ICON = {
  pending: Clock, contacted: Mail, won: Trophy, lost: XCircle,
};
const STATUS_COLOR = {
  pending: "bg-amber-500/15 border-amber-500/40 text-amber-300",
  contacted: "bg-blue-500/15 border-blue-500/40 text-blue-300",
  won: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
  lost: "bg-red-500/15 border-red-500/40 text-red-300",
};

const TYPE_LABEL = {
  enterprise: "Institución",
  partner: "Aliado",
  unknown: "Sin definir",
};
const USE_CASE_LABEL = {
  capacitar_equipo: "Capacitar equipo",
  lanzar_academia_whitelabel: "Lanzar academia white-label",
  integrar_api: "Integrar API",
  otro: "Otro",
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("es-MX", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function DemoRequestsAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: "100" });
    if (statusFilter) params.set("status", statusFilter);
    if (typeFilter) params.set("type", typeFilter);
    if (q) params.set("q", q);
    api.get(`/admin/demo-requests?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, typeFilter]);

  const patch = async (id, patch) => {
    try {
      const r = await api.patch(`/admin/demo-requests/${id}`, patch);
      toast.success("Actualizado");
      setDetail(r.data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const counts = data?.counts_by_status || {};
  const items = data?.items || [];

  return (
    <StaffLayout user={user}>
      <div data-testid="demo-requests-page">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">
              Fase 14.6 · Sales pipeline
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">
              Demo requests
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Solicitudes B2B y aliados que llegan desde <code className="text-brand-orange-500">/demo</code>
            </p>
          </div>
        </div>

        {/* Stats por status */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="demo-stats">
          {Object.entries(STATUS_LABEL).map(([code, label]) => {
            const Icon = STATUS_ICON[code];
            return (
              <button
                key={code}
                type="button"
                onClick={() => setStatusFilter(statusFilter === code ? "" : code)}
                data-testid={`stat-${code}`}
                className={`p-4 rounded-xl border text-left transition-colors ${
                  statusFilter === code
                    ? "bg-brand-orange-500/10 border-brand-orange-500"
                    : "bg-[#111827] border-gray-800 hover:border-gray-700"
                }`}
              >
                <Icon className="w-4 h-4 text-brand-orange-500" />
                <p className="mt-2 text-3xl font-bold text-white font-mono">{counts[code] || 0}</p>
                <p className="text-xs text-gray-400 uppercase tracking-widest">{label}</p>
              </button>
            );
          })}
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="demo-filters">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Buscar</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              data-testid="demo-search"
              placeholder="nombre, email, empresa"
              className="block mt-1 bg-[#111827] border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white w-64"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Tipo</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="demo-type-filter"
              className="block mt-1 bg-[#111827] border border-gray-800 rounded-md px-3 py-1.5 text-xs text-white"
            >
              <option value="">Todos</option>
              <option value="enterprise">Institución</option>
              <option value="partner">Aliado</option>
              <option value="unknown">Sin definir</option>
            </select>
          </div>
          <button
            type="button"
            onClick={load}
            className="px-3 py-1.5 rounded-md bg-brand-orange-500 hover:brightness-110 text-white text-xs font-semibold"
          >
            Aplicar
          </button>
        </div>

        {/* Tabla */}
        {loading ? (
          <div className="h-64 bg-[#111827] rounded-xl animate-pulse" />
        ) : items.length === 0 ? (
          <div className="p-10 border border-dashed border-gray-800 rounded-xl text-center">
            <Mail className="w-10 h-10 mx-auto text-gray-500" />
            <p className="mt-3 text-white font-semibold">Sin solicitudes</p>
            <p className="mt-1 text-sm text-gray-400">Cuando alguien llene el form en /demo aparecerá aquí.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-gray-800 rounded-xl" data-testid="demo-request-table">
            <table className="w-full text-sm">
              <thead className="bg-[#0F172A] text-xs uppercase tracking-widest text-gray-400">
                <tr>
                  <th className="text-left px-4 py-3">Fecha</th>
                  <th className="text-left px-4 py-3">Tipo</th>
                  <th className="text-left px-4 py-3">Contacto</th>
                  <th className="text-left px-4 py-3">Empresa</th>
                  <th className="text-left px-4 py-3">Use case</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const StatusIcon = STATUS_ICON[r.status] || Clock;
                  const isPartner = r.type === "partner";
                  return (
                    <tr key={r.id} className="border-t border-gray-800 hover:bg-white/5"
                          data-testid={`demo-row-${r.id}`}>
                      <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">
                        {fmtDate(r.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-widest font-semibold ${
                          isPartner
                            ? "bg-purple-500/15 border-purple-500/40 text-purple-300"
                            : "bg-blue-500/15 border-blue-500/40 text-blue-300"
                        }`}>
                          {isPartner ? <Handshake className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                          {TYPE_LABEL[r.type]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white font-semibold">{r.name}</p>
                        <p className="text-xs text-gray-400 font-mono">{r.work_email}</p>
                        <p className="text-[10px] text-gray-500">{r.role}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-white">{r.company}</p>
                        {r.employees_range && (
                          <p className="text-[10px] text-gray-400">
                            <Users className="w-3 h-3 inline" /> {r.employees_range}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-300">
                        {r.use_case ? USE_CASE_LABEL[r.use_case] || r.use_case : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border uppercase tracking-widest font-semibold ${STATUS_COLOR[r.status] || ""}`}>
                          <StatusIcon className="w-3 h-3" /> {STATUS_LABEL[r.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setDetail(r)}
                          data-testid={`demo-row-open-${r.id}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-gray-700 text-gray-300 hover:bg-white/10 text-xs font-semibold"
                        >
                          Detalle
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {detail && (
          <DetailModal item={detail} onClose={() => setDetail(null)} onSave={patch} />
        )}
      </div>
    </StaffLayout>
  );
}

function DetailModal({ item, onClose, onSave }) {
  const [status, setStatus] = useState(item.status);
  const [notes, setNotes] = useState(item.resolution_notes || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    await onSave(item.id, { status, resolution_notes: notes });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto"
        data-testid="demo-detail-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">
              {TYPE_LABEL[item.type]} · {fmtDate(item.created_at)}
            </p>
            <h2 className="text-xl font-bold text-white font-display mt-1">
              {item.company} · {item.name}
            </h2>
            <p className="text-xs text-gray-400 mt-1 font-mono">{item.work_email}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white text-xl">×</button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <InfoRow label="Cargo" value={item.role} />
          <InfoRow label="Teléfono" value={item.phone || "—"} />
          <InfoRow label="Tamaño equipo" value={item.employees_range || "—"} />
          <InfoRow label="Use case" value={USE_CASE_LABEL[item.use_case] || item.use_case || "—"} />
          <InfoRow label="Contactado" value={fmtDate(item.contacted_at)} />
          <InfoRow label="Última actualización" value={fmtDate(item.updated_at)} />
        </div>

        {item.message && (
          <div className="p-3 rounded-md bg-[#0F172A] border border-gray-800">
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-1">Mensaje</p>
            <p className="text-sm text-gray-200 whitespace-pre-wrap">{item.message}</p>
          </div>
        )}

        <div className="pt-3 border-t border-gray-800 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="demo-detail-status"
              className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white"
            >
              {Object.entries(STATUS_LABEL).map(([c, l]) => (
                <option key={c} value={c}>{l}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Notas internas</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={2000}
              data-testid="demo-detail-notes"
              placeholder="Contexto de la conversación, próximos pasos, propuesta enviada, etc."
              className="w-full mt-1 bg-[#0F172A] border border-gray-800 rounded-md px-3 py-2 text-sm text-white resize-y"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-white/5 text-sm"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={saving}
              data-testid="demo-detail-save"
              className="inline-flex items-center gap-1 px-6 py-2 rounded-md bg-brand-orange-500 hover:brightness-110 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</span>
      <span className="text-sm text-white">{value}</span>
    </div>
  );
}
